import type { Task } from '@/features/project-board/types';

/**
 * 요일별 처리량 · 주말 작업량.
 *
 * 상태가 바뀐 날짜(= PROGRESS.md `Updated At`)의 요일로 센다. 토·일에 상태가 바뀐 것을 주말 작업으로 본다
 * — 실제로 그날 일했다는 직접 증거는 아니지만(문서를 몰아서 갱신할 수도 있다), 팀이 주말에 얼마나
 * 움직이는지 보는 대리 지표로는 이게 유일하게 있는 데이터다.
 */
export interface WeekdayStats {
  /** 월~일 순. */
  days: Array<{ label: string; done: number; weekend: boolean }>;
  weekendDone: number;
  totalDone: number;
  weekendPct: number;
  /** 최근 8주의 주말 완료 — 최근에도 주말에 일하는지. */
  recentWeekendDone: number;
  recentWeeks: number;
}

const LABELS = ['월', '화', '수', '목', '금', '토', '일'];
const DAY = 86_400_000;

export class Weekday {
  static parseDate(value: string): Date | null {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  /** 월요일 0 … 일요일 6. JS의 0=일요일을 주 시작 월요일 기준으로 옮긴다. */
  static index(date: Date): number {
    return (date.getDay() + 6) % 7;
  }

  static statsOf(tasks: Task[], now: Date, recentWeeks = 8): WeekdayStats {
    const dates = tasks
      .filter((task) => task.status === 'done')
      .map((task) => Weekday.parseDate(task.updatedAt))
      .filter((date): date is Date => date !== null);

    const days = LABELS.map((label, index) => ({
      label,
      done: dates.filter((date) => Weekday.index(date) === index).length,
      weekend: index >= 5,
    }));

    const weekendDone = days.filter((day) => day.weekend).reduce((sum, day) => sum + day.done, 0);
    const since = now.getTime() - recentWeeks * 7 * DAY;

    return {
      days,
      weekendDone,
      totalDone: dates.length,
      weekendPct: dates.length === 0 ? 0 : Math.round((weekendDone / dates.length) * 100),
      recentWeekendDone: dates.filter((date) => date.getTime() >= since && Weekday.index(date) >= 5).length,
      recentWeeks,
    };
  }
}
